import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

interface PrivateRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

/**
 * PrivateRoute - Protects routes from unauthorized access
 * Redirects to /login if not authenticated
 * Optionally restricts access based on user roles
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return <Spin size="large" tip="Loading..." fullscreen />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role)) {
      // Redirect to appropriate dashboard based on user role
      const defaultRoutes: Record<UserRole, string> = {
        owner: '/owner/dashboard',
        advisor: '/advisor/dashboard',
        technician: '/technician/dashboard',
        customer: '/portal/dashboard',
      };

      return <Navigate to={defaultRoutes[user.role]} replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
